import { cn } from '@/utlis/cn'
import { LocalTrack } from 'livekit-client'
import { VideoHTMLAttributes, memo, useRef, useState, useMemo, useEffect } from 'react'

type VideoProps = VideoHTMLAttributes<HTMLVideoElement> & {
    srcObject?: MediaStream | null
    localTrack?: LocalTrack | null
    videoProcessor?: string | null
    tabIndex?: number
    isMirrored: boolean
    ignoreAspectRatio?: boolean
    className?: string
    autoPause?: number
}

const Video = memo(
    ({
        srcObject,
        tabIndex = undefined,
        isMirrored,
        localTrack,
        videoProcessor,
        ignoreAspectRatio,
        className,
        autoPause,
        ...props
    }: VideoProps) => {
        const videoRef = useRef<HTMLVideoElement>(null)
        const [defaultAspectRatio, setDefaultAspectRatio] = useState<string | undefined>()

        const memoizedVideo = useMemo(() => {
            const video = document.createElement('video')
            return video
        }, [localTrack, srcObject])

        useEffect(() => {
            const videoNode = videoRef.current
            if (!videoNode) return

            if (localTrack) {
                localTrack.attach(memoizedVideo)
                videoNode.srcObject = memoizedVideo.srcObject
                const aspectRatio = localTrack.mediaStreamTrack.getSettings().aspectRatio
                setDefaultAspectRatio(aspectRatio ? `${aspectRatio}/1` : '16/9')
            } else if (srcObject) {
                videoNode.srcObject = srcObject
                const aspectRatio = srcObject.getVideoTracks()[0].getSettings().aspectRatio
                setDefaultAspectRatio(aspectRatio ? `${aspectRatio}/1` : '16/9')
            } else {
                console.warn('No srcObject or localTrack provided')
            }

            videoNode.setAttribute('autoplay', '')
            videoNode.setAttribute('muted', '')
            videoNode.setAttribute('playsinline', '')

            let pauseTimer: number | undefined
            if (autoPause) {
                pauseTimer = window.setTimeout(() => {
                    videoNode.pause()
                }, autoPause)
            }

            return () => {
                if (videoNode) {
                    localTrack?.detach(memoizedVideo)
                    videoNode.srcObject = null
                }
                if (pauseTimer) {
                    clearTimeout(pauseTimer)
                }
            }
        }, [srcObject, localTrack, memoizedVideo, videoProcessor])

        return (
            <video
                ref={videoRef}
                tabIndex={tabIndex}
                {...props}
                className={cn(
                    isMirrored ? '-scale-x-100' : undefined,
                    ignoreAspectRatio ? undefined : `aspect-[${defaultAspectRatio}]`,
                    className
                )}
            />
        )
    }
)

export default Video
